import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import * as BS from 'react-bootstrap';
import * as BSEx from '../../BootstrapExtends';
import Box from '../../atoms/Box';
import Sheet from '../../../models/SpreadsheetReportConfigs/Sheet';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { generateUniqueId } from '../../../lib/StringUtils.js';

const scope =
  'frontend.components.SpreadsheetReportConfigs.Form.SheetMappingForm';

const propTypes = {
  apiFetchingList: PropTypes.arrayOf(PropTypes.string).isRequired,
  spreadsheetReportConfigs: PropTypes.object.isRequired,
  currentSheetList: PropTypes.arrayOf(PropTypes.shape(Sheet.propTypes)),
  spreadsheetId: PropTypes.string,
  isRenewConnect: PropTypes.bool,
  sheetMapping: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
};

const deepClone = (value) => {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => deepClone(item));
  }
  if (value && typeof value === 'object') {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = deepClone(value[key]);
      return acc;
    }, {});
  }
  return value;
};

const clonePanelWithNewIds = (panel) => {
  const clonedPanel = deepClone(panel);
  clonedPanel.id = generateUniqueId();
  if (Array.isArray(clonedPanel.columns)) {
    clonedPanel.columns = clonedPanel.columns.map((column) => ({
      ...column,
      id: generateUniqueId(),
    }));
  }
  return clonedPanel;
};

const SheetMappingForm = ({
  apiFetchingList,
  spreadsheetReportConfigs,
  currentSheetList,
  spreadsheetId,
  isRenewConnect,
  sheetMapping,
  onSubmit,
}) => {
  const validationSchema = Yup.object().shape(
    Object.fromEntries(
      currentSheetList.map((sheet) => [sheet.title, Yup.string()])
    )
  );

  const sourceSheetKeySet = new Set(
    spreadsheetReportConfigs?.panels?.sortedPanelKeys ||
      Object.keys(spreadsheetReportConfigs?.panels?.items || {})
  );

  const initialValues =
    isRenewConnect && sheetMapping
      ? sheetMapping
      : Object.fromEntries(
          currentSheetList.map((sheet) => [
            sheet.title,
            sourceSheetKeySet.has(sheet.title) ? sheet.title : '',
          ])
        );

  const duplicatePanels = (panelKey) => {
    const sourcePanels = spreadsheetReportConfigs.panels.items[panelKey] ?? [];
    return sourcePanels.map((panel) => clonePanelWithNewIds(panel));
  };

  const handleSubmit = (values) => {
    const newPanelsItems = Object.fromEntries(
      Object.entries(values).map(([title, panelKey]) => [
        title,
        panelKey ? duplicatePanels(panelKey) : [],
      ])
    );
    onSubmit({
      spreadsheetReportConfigs: {
        ...spreadsheetReportConfigs,
        sheetId: spreadsheetId,
        panels: {
          items: newPanelsItems,
          sortedPanelKeys: currentSheetList.map((sheet) => sheet.title),
        },
      },
      sheetMapping: values,
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, handleChange, handleSubmit }) => (
        <BS.Form horizontal onSubmit={handleSubmit}>
          <Box>
            <Box.Body>
              <BS.Row style={{ marginBottom: '15px' }}>
                <BS.Col
                  sm={2}
                  style={{ fontWeight: 'bold', textAlign: 'right' }}
                >
                  {I18n.t(`${scope}.field.currentSheetName.label`)}
                </BS.Col>
                <BS.Col sm={10} style={{ fontWeight: 'bold' }}>
                  {I18n.t(`${scope}.field.sourceSheetName.label`)}
                </BS.Col>
              </BS.Row>
              {currentSheetList.map((sheet) => (
                <Fragment key={sheet.title}>
                  <BSEx.FormGroupInline label={sheet.title}>
                    <BS.FormControl
                      componentClass='select'
                      value={values[sheet.title]}
                      name={sheet.title}
                      onChange={handleChange}
                    >
                      <option value=''>
                        {I18n.t(`${scope}.field.sourceSheetName.placeholder`)}
                      </option>
                      {spreadsheetReportConfigs.panels.sortedPanelKeys.map(
                        (panelKey) => (
                          <option key={panelKey} value={panelKey}>
                            {panelKey}
                          </option>
                        )
                      )}
                    </BS.FormControl>
                  </BSEx.FormGroupInline>
                </Fragment>
              ))}
            </Box.Body>

            <Box.Footer>
              <BSEx.FormGroup>
                <BS.Col smOffset={2} sm={10}>
                  <p style={{ marginBottom: '8px', color: '#000' }}>
                    {I18n.t(`${scope}.messages.panelOverwriteWarning`)}
                  </p>
                  <BS.ButtonToolbar>
                    <BS.Button
                      disabled={apiFetchingList.length > 0}
                      bsStyle='primary'
                      type='submit'
                    >
                      {I18n.t(`${scope}.action.reflect`)}
                    </BS.Button>
                    <BS.Button
                      bsStyle='default'
                      type='button'
                      onClick={() => history.back()}
                    >
                      {I18n.t(`${scope}.action.back`)}
                    </BS.Button>
                  </BS.ButtonToolbar>
                </BS.Col>
              </BSEx.FormGroup>
            </Box.Footer>
          </Box>
        </BS.Form>
      )}
    </Formik>
  );
};

SheetMappingForm.propTypes = propTypes;

export default SheetMappingForm;
